import { useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { z } from "zod";

// Validation schema
const leadSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  phone: z.string().trim().min(1, "Phone is required").max(20),
  email: z.string().trim().email().max(255).optional().or(z.literal("")),
  company: z.string().trim().max(200).optional().or(z.literal("")),
  message: z.string().trim().max(2000).optional().or(z.literal("")),
});

// Map routes to readable page names
const getPageName = (pathname: string): string => {
  const pageMap: Record<string, string> = {
    "/": "homepage",
    "/marketing/smm": "smm",
    "/marketing/research": "market-research",
    "/solutions/crm": "crm",
    "/solutions/automation": "automation",
    "/solutions/bi-dashboards": "bi-dashboards",
    "/solutions/custom": "custom-solutions",
    "/contact": "contact",
  };
  return pageMap[pathname] || pathname.replace(/\//g, "-").slice(1) || "unknown";
};

// Get UTM parameters from URL
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "website",
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
  };
};

// Send email notification about new lead
const sendEmailNotification = async (leadData: {
  name: string;
  phone: string;
  email?: string | null;
  company?: string | null;
  message?: string | null;
  form_type: string;
  source_page: string;
  page_url: string;
}) => {
  try {
    const { error } = await supabase.functions.invoke("send-lead-notification", {
      body: {
        name: leadData.name,
        phone: leadData.phone,
        email: leadData.email || undefined,
        company: leadData.company || undefined,
        message: leadData.message || undefined,
        form_type: leadData.form_type,
        source_page: leadData.source_page,
        page_url: leadData.page_url,
      },
    });

    if (error) {
      console.error("Failed to send email notification:", error);
    }
  } catch (error) {
    // Don't fail the lead submission if email fails
    console.error("Email notification error:", error);
  }
};

interface LeadFormData {
  name: string;
  phone: string;
  email?: string;
  company?: string;
  message?: string;
}

interface UseLeadSubmissionOptions {
  formType: string;
}

export function useLeadSubmission({ formType }: UseLeadSubmissionOptions) {
  const { t } = useLanguage();
  const { toast } = useToast();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitLead = async (formData: LeadFormData): Promise<boolean> => {
    // Validate input
    const validation = leadSchema.safeParse(formData);
    if (!validation.success) {
      toast({
        title: t("budget.form.error"),
        description: t("budget.form.errorDescription"),
        variant: "destructive",
      });
      return false;
    }

    setIsSubmitting(true);

    try {
      const utmParams = getUtmParams();
      const sourcePage = getPageName(location.pathname);

      const leadData = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email?.trim() || null,
        company: formData.company?.trim() || null,
        message: formData.message?.trim() || null,
        source_page: sourcePage,
        form_type: formType,
        page_url: window.location.href,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium || formType,
        utm_campaign: utmParams.utm_campaign || sourcePage,
        status: "new",
      };

      const { error } = await supabase.from("leads").insert(leadData);

      if (error) {
        throw error;
      }

      // Send email notification (don't await to not block the user)
      sendEmailNotification({
        name: leadData.name,
        phone: leadData.phone,
        email: leadData.email,
        company: leadData.company,
        message: leadData.message,
        form_type: formType,
        source_page: sourcePage,
        page_url: window.location.href,
      });

      toast({
        title: t("budget.form.success"),
        description: t("budget.form.successDescription"),
      });

      return true;
    } catch (error) {
      // Only log errors in development to prevent information leakage
      if (import.meta.env.DEV) {
        console.error("Error submitting lead:", error);
      }
      toast({
        title: t("budget.form.error"),
        description: "Произошла ошибка при отправке. Попробуйте позже.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitLead, isSubmitting };
}
